import { useState } from 'react';
import { Plural, T, Var } from 'gt-react';
import type { ActivityFieldDiff } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import { formatFieldValue } from './formatFieldValue';

function isStructured(value: unknown): boolean {
  return value !== null && typeof value === 'object';
}

/** Pretty-printed JSON for objects and arrays; scalars as the inline diff shows them. */
function formatStructured(value: unknown): string {
  return isStructured(value)
    ? JSON.stringify(value, null, 2)
    : formatFieldValue(value);
}

interface ActivityFieldDiffsProps {
  changedFields: Record<string, ActivityFieldDiff> | undefined;
  /** Display name for a column; defaults to the raw column name. */
  labelFor?: (field: string) => string;
  /**
   * Show object/array values as side-by-side pretty JSON blocks instead of
   * one inline line, and list the changed fields above the toggle.
   */
  expandStructured?: boolean;
}

/** A collapsible old → new list of the columns an audit entry changed. */
export function ActivityFieldDiffs({
  changedFields,
  labelFor,
  expandStructured,
}: ActivityFieldDiffsProps) {
  const [open, setOpen] = useState(false);
  const label = (field: string) => labelFor?.(field) ?? field;
  const diffFields = Object.entries(changedFields ?? {});
  if (diffFields.length === 0) return null;
  return (
    <div className={expandStructured ? 'mt-0.5' : 'mt-1'}>
      {expandStructured && (
        <div className="text-xs text-muted-foreground">
          {diffFields.map(([field]) => label(field)).join(', ')}
        </div>
      )}
      <Button
        variant="link"
        size="sm"
        className="h-auto p-0 text-xs"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? (
          <T context="button, collapse the field-level diff of an activity entry">
            Hide changes
          </T>
        ) : (
          <T context="button, expand the field-level diff of an activity entry">
            Show <Var>{diffFields.length}</Var>{' '}
            <Plural
              n={diffFields.length}
              one="changed field"
              other="changed fields"
            />
          </T>
        )}
      </Button>
      {open && (
        <div className="mt-1 space-y-0.5 rounded-md border bg-muted/40 px-3 py-2">
          {diffFields.map(([field, diff]) =>
            expandStructured &&
            (isStructured(diff.old) || isStructured(diff.new)) ? (
              <div key={field} className="py-0.5 text-xs">
                <div className="text-muted-foreground">{label(field)}</div>
                <div className="grid gap-1 sm:grid-cols-2">
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-red-50 p-1.5 font-mono text-red-700 dark:bg-red-950/40 dark:text-red-300">
                    {formatStructured(diff.old)}
                  </pre>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-green-50 p-1.5 font-mono text-green-700 dark:bg-green-950/40 dark:text-green-300">
                    {formatStructured(diff.new)}
                  </pre>
                </div>
              </div>
            ) : (
              <div
                key={field}
                className="flex items-start gap-2 font-mono text-xs"
              >
                <span className="text-muted-foreground">{label(field)}:</span>
                <span className="break-all text-red-600">
                  {formatFieldValue(diff.old)}
                </span>
                <span className="text-muted-foreground">→</span>
                <span className="break-all text-green-600">
                  {formatFieldValue(diff.new)}
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}
